'use client';

import classnames from 'classnames';
import { useLocale, useTranslations } from 'next-intl';
import React, { useState } from 'react';

import { HighlightItem } from '@/app/[locale]/(default-layout)/specials/_components/SpecialsHeroContent';

import { ShowingInfoModal } from '@/components/showing-details/ShowingInfoModal';
import TicketLink from '@/components/ticket-shape/TicketLink';

import { getShowingPropertiesLanguage, getShowingTechnologies } from '@cineamo/ui/src/helpers/showing-helper';

import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import { IconInfo16 } from '@cineamo/ui/src/icons/ic-info';
import { IconTicket2_24 } from '@cineamo/ui/src/icons/ic-ticket';

import MobileOrDesktopRenderer from '@cineamo/composables/src/MobileOrDesktopRenderer';

import { useCinemaRooms } from '@/providers/CinemaRoomProvider';
import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { useShowingTags } from '@/providers/ShowingTagProvider';

type ProgramHighlightCardProps = Readonly<{
    highlight: HighlightItem;
    className?: string;
}>;

export default function ProgramHighlightCard({ highlight, className }: ProgramHighlightCardProps): React.JSX.Element {
    const domainConfiguration = useDomainConfiguration();
    const { getCinemaRoomById } = useCinemaRooms();
    const { getShowingTagById } = useShowingTags();
    const t = useTranslations();
    const locale = useLocale();
    const format = useFormat({ timeZone: domainConfiguration.timeZone });
    const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

    const { showing, content } = highlight;

    const weekday = format.dateTime.formatWeekday(showing.startDatetime, 'short').replace('.', '');
    const date = format.dateTime.formatDayMonth(showing.startDatetime);
    const time = format.dateTime.formatDate_HHmm24(showing.startDatetime);

    const cinemaRoom = getCinemaRoomById(showing.cinemaRoomId);
    const cinemaRoomName = cinemaRoom?.name;

    const is3D = showing.isThreeDimensional;
    const languageInfo = getShowingPropertiesLanguage(showing, locale);
    const technologies = getShowingTechnologies(showing).map((tech: string) =>
        t(`showtimes.showing-info.technology.${tech}`)
    );

    const screeningInfoParts: string[] = [
        cinemaRoomName,
        is3D ? '3D' : undefined,
        ...languageInfo,
        ...technologies
    ].filter((it): it is string => !!it);

    const showingTags =
        showing.showingTagIds?.map((tagId) => {
            return getShowingTagById(tagId) ?? null;
        }) ?? [];

    const primaryColor = domainConfiguration.style.primaryColor;
    const onPrimaryColor = domainConfiguration.style.onPrimaryColor;

    return (
        <>
            <div
                className={classnames(
                    'text-left rounded-8 p-8',
                    'grid gap-x-12 gap-y-4',
                    'grid-cols-[80px_minmax(0,1fr)_auto] lg:grid-cols-[96px_minmax(0,1fr)_auto]',
                    'grid-rows-[auto_auto]',
                    'bg-[rgba(var(--primary-color-rgb),0.1)]',
                    className
                )}>
                {/* Date & Time column */}
                <div className="col-start-1 row-start-1 flex flex-col items-start justify-center pt-2">
                    <span className="text-body-bold-12 lg:text-body-bold-14 truncate w-full leading-[18px] lg:leading-[24px]">
                        {weekday}, {date}
                    </span>
                    <span className="text-body-bold-12 lg:text-body-bold-14 leading-[18px] lg:leading-[24px]">
                        {time}
                    </span>
                </div>

                {/* Movie info column */}
                <div className="col-start-2 row-start-1 flex flex-col items-start justify-center">
                    <span className="text-body-bold-14 lg:text-body-bold-16 truncate w-full">{content.title}</span>
                    <div className="flex items-center gap-6 text-body-12 flex-wrap">
                        {screeningInfoParts.map((part, index) => (
                            <React.Fragment key={index}>
                                {index > 0 && <span className="opacity-50">|</span>}
                                <span className="whitespace-nowrap">{part}</span>
                            </React.Fragment>
                        ))}
                        <button
                            type="button"
                            className="shrink-0 cursor-pointer opacity-50 hover:opacity-100 transition-opacity"
                            onClick={() => setIsInfoModalOpen(true)}>
                            <IconInfo16 className="size-14" />
                        </button>
                    </div>
                </div>

                {/* Tickets button column */}
                <div className="col-start-3 row-start-1 self-start">
                    <TicketLink
                        showing={showing}
                        content={content}>
                        <MobileOrDesktopRenderer
                            desktopContent={
                                <span
                                    className="inline-flex items-center gap-8 px-12 py-6 rounded-4 text-body-bold-14 cursor-pointer"
                                    style={{
                                        backgroundColor: primaryColor,
                                        color: onPrimaryColor
                                    }}>
                                    <IconTicket2_24
                                        className="size-20"
                                        fill={onPrimaryColor}
                                    />
                                    {t('tickets.tickets')}
                                </span>
                            }
                            mobileContent={
                                <span
                                    className="inline-flex items-center justify-center size-40 rounded-4 cursor-pointer"
                                    style={{
                                        backgroundColor: primaryColor,
                                        color: onPrimaryColor
                                    }}>
                                    <IconTicket2_24
                                        className="size-20"
                                        fill={onPrimaryColor}
                                    />
                                </span>
                            }
                        />
                    </TicketLink>
                </div>

                {/* Highlight text row */}
                {showing.highlightText && (
                    <div className="col-span-3 lg:col-start-2 lg:col-span-2 row-start-2 pr-4">
                        <p className="text-body-12 opacity-70">
                            <span
                                className="font-semibold"
                                style={{ color: primaryColor }}>
                                {t('showtimes.highlight')}:
                            </span>{' '}
                            {showing.highlightText}
                        </p>
                    </div>
                )}
            </div>

            {isInfoModalOpen && (
                <ShowingInfoModal
                    showing={showing}
                    content={content}
                    modalIsOpen={isInfoModalOpen}
                    setModalIsOpen={(val: boolean) => setIsInfoModalOpen(val)}
                    specials={showingTags}
                    onClose={() => setIsInfoModalOpen(false)}
                    onShare={() => {}}
                    onSubmit={() => {}}
                />
            )}
        </>
    );
}
